import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Modal } from "../../Modal/Modal";
import { useTranslation } from "react-i18next";
import { useProductBase } from "../../../hooks/useProductBase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useUserPartnerInformation } from "../../../hooks";
import { auth } from "../../../firebase";
import { CATEGORIES } from "../../../constants/categories";
import {
  addToProductBase,
  editProduct,
  getProductBySku,
} from "../../../services/product/product.api";
import { ProductPayload } from "../../../types";
import { Loading } from "../../../components/Loading/Loading";


const ProductModal = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(id === "new");
  const [user] = useAuthState(auth);
  const { userPartnerInformation } = useUserPartnerInformation(user?.uid);
  const [priceCurrency, setPriceCurrency] = useState("");
  const [priceValue, setPriceValue] = useState("");
  const [sku, setSku] = useState("");
  const [brand, setBrand] = useState("");

  const [model, setModel] = useState("");
  const [mainCategory, setMainCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [edition, setEdition] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const categories: { [key: string]: string[] } = CATEGORIES;
  const partnerId = userPartnerInformation?.data?.partnerId;

  useEffect(() => {
    const loadProduct = async () => {
      if (id !== "new" && typeof partnerId === "string") {
        setIsLoading(true);
        setFetchError(null);

        try {
          const response = await getProductBySku(partnerId, `${id}`);
          const product = response?.data;

          if (product) {
            setPriceCurrency(product.price?.currency || "");
            setPriceValue(product.price?.value || "");
            setSku(product.sku || "");
            setBrand(product.brand.name || "");
            setDescription(product.description || "");
            setModel(product.model || "");
            setSubCategory(product.category?.subCategory || "");
            setMainCategory(product.category?.mainCategory || "");
            setEdition(product.edition || "");
            setIsEditing(false);
          } else {
            setFetchError("Product not found.");
          }
        } catch (error: any) {
          console.error("Error fetching product:", error);
          setFetchError("Failed to load product information.");
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadProduct();
  }, [id, partnerId]);

  const handleSave = async () => {
    if (typeof partnerId !== "string") {
      console.error("Partner ID is missing or invalid.");
      alert("Unable to save the product. Missing partner information.");
      return;
    }

    if (typeof id !== "string") {
      console.error("Product ID is missing or invalid.");
      alert("Unable to save the product. Invalid product ID.");
      return;
    }

    const payload: ProductPayload = {
      brand,
      model,
      category: {
        mainCategory,
        subCategory,
      },
      edition,
      price: {
        currency: priceCurrency,
        value: priceValue,
      },
      sku,
      description,
    };

    try {
      if (id === "new") {
        await addToProductBase(partnerId, payload);
      } else {
        await editProduct(id, partnerId, payload);
      }

      // ✅ Redirect and force reload
      window.location.href = "/product-base";
    } catch (error) {
      console.error("Error saving product:", error);
      alert("Failed to save product. Please try again.");
    }
  };

  return (
    <Modal visible showClose backdropClose={false} onClose={() => navigate(-1)}>
      <div className="w-screen md:w-[1050px] sm:w-[400px]">
        {isLoading && (
          <div className="p-4 text-center">
            <Loading />
            <p>Loading product information...</p>
          </div>
        )}

        {fetchError && (
          <div className="p-4 text-red-600 text-center font-semibold">
            {fetchError}
          </div>
        )}

        <div className="flex justify-between items-center p-4">
          <h1 className="text-xl font-bold">
            {id === "new" ? "Create New Product" : "Product Information"}
          </h1>
          {id !== "new" && !isEditing && (
            <button
              className="text-blue-600 font-semibold"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          )}
        </div>

        <div className="p-6 bg-white rounded-md shadow-md">
          <form className="grid grid-cols-2 gap-4">
            {
              <div className="col-span-2">
                <label className="text-sm font-semibold">SKU</label>
                <input
                  type="text"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  required
                  disabled
                  className={`w-full border border-gray-300 p-2 rounded ${"bg-gray-100 text-gray-500 cursor-not-allowed"}`}
                />
              </div>
            }

            <div>
              <label className="text-sm font-semibold">Brand</label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                required
                disabled
                className={`w-full border border-gray-300 p-2 rounded ${"bg-gray-100 text-gray-500 cursor-not-allowed"}`}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Model</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                required
                disabled
                className={`w-full border border-gray-300 p-2 rounded ${"bg-gray-100 text-gray-500 cursor-not-allowed"}`}
              />
            </div>

            <div>
              <label className="text-sm font-semibold">Main Category</label>
              <select
                value={mainCategory}
                onChange={(e) => {
                  setMainCategory(e.target.value);
                  setSubCategory("");
                }}
                required
                disabled={!isEditing}
                className={`w-full border border-gray-300 p-2 rounded ${!isEditing
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : ""
                  }`}
              >
                <option value="">Select Main Category</option>
                {Object.keys(categories).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold">Sub Category</label>
              <select
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
                disabled={!mainCategory || !isEditing}
                className={`w-full border border-gray-300 p-2 rounded ${!isEditing
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : ""
                  }`}
              >
                <option value="">Select Sub Category</option>
                {mainCategory &&
                  categories[mainCategory]?.map((sub) => (
                    <option key={sub} value={sub}>
                      {sub}
                    </option>
                  ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="text-sm font-semibold">Description</label>
              <textarea
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={!isEditing}
                placeholder="Enter product description..."
                className={`w-full border border-gray-300 p-2 rounded resize-none ${!isEditing
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : ""
                  }`}
              />
            </div>

            <div className="col-span-2">
              <label className="text-sm font-semibold">Edition</label>
              <input
                type="text"
                value={edition}
                onChange={(e) => setEdition(e.target.value)}
                disabled={!isEditing}
                className={`w-full border border-gray-300 p-2 rounded ${!isEditing
                    ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                    : ""
                  }`}
              />
            </div>
          </form>

          {(isEditing || id === "new") && (
            <div className="mt-6 text-right">
              <button
                onClick={handleSave}
                className=" bg-indigo-300 hover:bg-indigo-400 text-white px-4 py-2 rounded"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ProductModal;
